/**
 * Shopify Forms paints its teaser and popup inside an open shadow root.
 * The 10% off chip and the modal panel use the theme paper.
 * Text fields stay clear so that paper shows through.
 * Titles use the subheading font. Copy, fields, and submit use the body font.
 * The Shopify Inbox chat panel uses the same paper.
 */
(function () {
  function themeFont(variableName) {
    var value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    if (!value || /[{};<>]/.test(value)) return "sans-serif";
    return value;
  }

  var imageUrl = document.documentElement.getAttribute("data-gpw-paper-image") || "";
  if (/["\\\n\r)]/.test(imageUrl)) imageUrl = "";

  function themeColor(variableName, fallback) {
    var value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    if (!value || /[{};<>]/.test(value)) return fallback;
    return value;
  }

  var headingFont = themeFont("--font-subheading--family");
  var bodyFont = themeFont("--font-body--family");
  var buttonColor = themeColor("--gpw-terracotta", "#9e3c1f");
  var buttonHover = themeColor("--gpw-terracotta-hover", "#7f3018");
  var paperImage = imageUrl
    ? "linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),url(\"" + imageUrl + "\")"
    : "none";
  var css =
    ":host{" +
    "--container-background-color:#e8dbc8 !important;" +
    "--form-background-color:transparent !important;" +
    "--forms-heading-font:600 1.375rem/1.2 " + headingFont + " !important;" +
    "--forms-body-font:400 0.9375rem/1.6 " + bodyFont + " !important;" +
    "--button-background-color:" + buttonColor + " !important;" +
    "--button-text-color:#ffffff !important;" +
    "}" +
    'button[data-testid="form-teaser"],[class*="formContainer"]{' +
    "background-color:#e8dbc8 !important;" +
    "background-image:" + paperImage + " !important;" +
    "background-size:cover !important;" +
    "background-position:center !important;" +
    "background-repeat:no-repeat !important;" +
    "}" +
    ':is(input,textarea,[class*="formInput"],[class*="formPhone"],[class*="textArea"],[class*="selectToggle"]){' +
    "background-color:transparent !important;" +
    "background-image:none !important;" +
    "}" +
    ':is(h1,h2,h3,[class*="teaserHeading"],[class*="Heading"]){' +
    "font-family:" + headingFont + " !important;" +
    "font-weight:600 !important;" +
    "letter-spacing:0.1em !important;" +
    "text-transform:uppercase !important;" +
    "}" +
    ':is(p,label,input,textarea,button,[class*="formSubmit"],[class*="disclaimer"]){' +
    "font-family:" + bodyFont + " !important;" +
    "}" +
    ':is(button,[class*="formSubmit"]){' +
    "letter-spacing:0.12em !important;" +
    "text-transform:uppercase !important;" +
    "}" +
    '[class*="formSubmit"]{' +
    "background-color:" + buttonColor + " !important;" +
    "background-image:none !important;" +
    "color:#ffffff !important;" +
    "border-color:" + buttonColor + " !important;" +
    "}" +
    '[class*="formSubmit"]:hover{' +
    "background-color:" + buttonHover + " !important;" +
    "border-color:" + buttonHover + " !important;" +
    "filter:none !important;" +
    "}";

  function paint(root) {
    if (!root || root.querySelector("#gpw-forms-paper")) return;
    var style = document.createElement("style");
    style.id = "gpw-forms-paper";
    style.textContent = css;
    root.appendChild(style);
  }

  function watch(host) {
    if (host.dataset.gpwPaperWatch) return;
    host.dataset.gpwPaperWatch = "pending";

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      if (!host.shadowRoot) {
        if (attempts > 120) window.clearInterval(timer);
        return;
      }

      window.clearInterval(timer);
      host.dataset.gpwPaperWatch = "done";
      paint(host.shadowRoot);
      new MutationObserver(function () {
        if (host.shadowRoot && !host.shadowRoot.querySelector("#gpw-forms-paper")) {
          paint(host.shadowRoot);
        }
      }).observe(host.shadowRoot, { childList: true });
    }, 250);
  }

  function paintChat(root) {
    if (!root || root.querySelector("#gpw-chat-paper")) return;
    var style = document.createElement("style");
    style.id = "gpw-chat-paper";
    style.textContent =
      ".panel{" +
      "background-color:#e8dbc8 !important;" +
      "background-image:" + paperImage + " !important;" +
      "background-size:cover !important;" +
      "background-position:center !important;" +
      "background-repeat:no-repeat !important;" +
      "}" +
      ".activator{" +
      "min-height:44px !important;" +
      "padding:0.75rem 1.5rem !important;" +
      "border:1px solid " + buttonColor + " !important;" +
      "border-radius:4px !important;" +
      "background-color:" + buttonColor + " !important;" +
      "background-image:none !important;" +
      "color:#ffffff !important;" +
      "box-shadow:none !important;" +
      "}" +
      ".activator:hover,.activator:focus-visible{" +
      "background-color:" + buttonHover + " !important;" +
      "border-color:" + buttonHover + " !important;" +
      "}" +
      ".activator:focus-visible{" +
      "outline:2px solid " + buttonColor + " !important;" +
      "outline-offset:3px !important;" +
      "}" +
      ".activator__label{" +
      "font-family:" + headingFont + " !important;" +
      "font-size:0.75rem !important;" +
      "font-weight:700 !important;" +
      "letter-spacing:0.12em !important;" +
      "text-transform:uppercase !important;" +
      "color:#ffffff !important;" +
      "}";
    root.appendChild(style);
  }

  function watchChat(host) {
    if (host.dataset.gpwChatWatch) return;
    host.dataset.gpwChatWatch = "pending";

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      var runtime = host.shadowRoot && host.shadowRoot.querySelector("shopify-agent-runtime");
      var root = runtime && runtime.shadowRoot;
      if (!root) {
        if (attempts > 120) window.clearInterval(timer);
        return;
      }

      window.clearInterval(timer);
      host.dataset.gpwChatWatch = "done";
      paintChat(root);
      new MutationObserver(function () {
        if (runtime.shadowRoot && !runtime.shadowRoot.querySelector("#gpw-chat-paper")) {
          paintChat(runtime.shadowRoot);
        }
      }).observe(root, { childList: true });
    }, 250);
  }

  function scan() {
    document.querySelectorAll("shopify-forms-embed").forEach(watch);
    document.querySelectorAll("shopify-chat").forEach(watchChat);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan);
  } else {
    scan();
  }

  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
